import {
  User,
  UserCredential,
  createUserWithEmailAndPassword,
  getAuth,
  sendPasswordResetEmail,
  signInAnonymously,
  signInWithEmailAndPassword,
  signOut
} from "firebase/auth";
import { FacebookAuthProvider, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { CurrentUser, FirebaseRefs, UserRoles } from "interfaces";
import { UseFormSetError } from "react-hook-form";
import { toast } from "react-toastify";
import shortid from "shortid";
import { unwrapAPIError, unwrapFirebaseAuthError } from "utils";
import { auth, db } from "utils/firebase";

type AuthTypes = {
  role?: UserRoles;
  authType: "login" | "register";
  providerType: "google" | "facebook";
};

const googleProvider = new GoogleAuthProvider();
const facebookProvider = new FacebookAuthProvider();

const provider = {
  google: googleProvider,
  facebook: facebookProvider
};

const saveUser = async ({ user }: { user: User }) => {
  const document = {
    email: user.email!,
    role: UserRoles.user,
    uid: user.uid
  };

  await setDoc(doc(db, FirebaseRefs.USERS, document.uid), {
    createdAt: serverTimestamp(),
    ...document
  });

  return {
    user: {
      ...document
    } as CurrentUser,
    accessToken: (user as User & { accessToken: string })?.accessToken ?? ""
  };
};

const getUser = async ({ user }: { user: User }) => {
  const loginDocRef = doc(db, FirebaseRefs.USERS, user.uid!);
  const loginDocSnap = await getDoc(loginDocRef);

  // login details
  if (loginDocSnap.exists()) {
    return {
      user: {
        uid: user.uid,
        ...loginDocSnap.data()
      } as CurrentUser,
      accessToken: (user as User & { accessToken: string })?.accessToken ?? ""
    };
  }
  throw new Error("Login failed");
};

const thirdPartyAuth = async ({ authType, providerType }: AuthTypes) => {
  try {
    const result = await signInWithPopup(auth, provider[providerType]);
    // const credential = GoogleAuthProvider.credentialFromResult(result)
    // const token = credential?.accessToken
    // The signed-in user info.
    const user = result.user;
    const toastId = shortid.generate();
    let response: { user: CurrentUser; accessToken: string };

    if ((result as UserCredential & { _tokenResponse: { isNewUser: boolean } })._tokenResponse?.isNewUser) {
      toast.loading("Signing Up...", {
        position: "top-center",
        toastId
      });
      response = await saveUser({ user });
    } else {
      const tstMsg = authType === "register" ? "We found an account, Signing In..." : "Signing In...";

      toast.loading(tstMsg, {
        position: "top-center",
        toastId
      });

      response = await getUser({ user });
    }
    toast.dismiss(toastId);
    return response;
  } catch (error: any) {
    throw Error(unwrapAPIError(error));
  }
};

const register = async (
  payload: { email: string; password: string } & {
    setError: UseFormSetError<{
      email: string;
      password: string;
    }>;
  }
) => {
  try {
    const { user } = await createUserWithEmailAndPassword(auth, payload.email, payload.password);

    return await saveUser({ user });
  } catch (error: any) {
    unwrapFirebaseAuthError(error, payload.setError);
    throw Error(error);
  }
};

const login = async (
  payload: { email: string; password: string } & {
    setError: UseFormSetError<{
      email: string;
      password: string;
    }>;
  }
) => {
  try {
    const { user } = await signInWithEmailAndPassword(auth, payload.email, payload.password);

    return await getUser({ user });
  } catch (error: any) {
    unwrapFirebaseAuthError(error, payload.setError);
    throw Error(error);
  }
};

type resetPasswordTypes = {
  email: string;
  onSuccess: () => void;
  onError: (arg: string) => void;
};

const anonymousLogin = async () => {
  try {
    const { user } = await signInAnonymously(auth);

    return user;
  } catch (error: any) {
    console.error(error);
    throw Error(error);
  }
};

const deleteUser = async (user$: User) => {
  await user$.delete();
};

const resetPassword =
  ({ email, onSuccess, onError }: resetPasswordTypes) =>
  async () => {
    const auth = getAuth();
    sendPasswordResetEmail(auth, email)
      .then(() => {
        onSuccess();
      })
      .catch(error => {
        // const errorCode = error.code;
        const errorMessage = error.message;
        onError(errorMessage);
        // ..
        console.error("....error", error);
        throw Error(error);
      });
  };

const logOut = async () => {
  try {
    await signOut(auth);
    return;
  } catch (error: any) {
    throw Error(error);
  }
};

export const checkAuthState = async (data: { uid: string; role: UserRoles }): Promise<CurrentUser | null> => {
  const { uid } = data;

  const loginDocRef = doc(db, FirebaseRefs.USERS, uid);
  const loginDocSnap = await getDoc(loginDocRef);
  return loginDocSnap.data() as CurrentUser;
};

export const AuthService = {
  resetPassword,
  logOut,
  login,
  anonymousLogin,
  deleteUser,
  thirdPartyAuth,
  register,
  checkAuthState
};
